import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Input,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormHelperText,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { enqueueSnackbar } from "notistack";
import GeneralCkeditor from "../../../../../components/editors/GeneralCkEditor";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { s3baseUri } from "../../../../../config/config";
import { upload_image_documents_on_s3 } from "../../../../../DAL/jobs/jobs";
import { useParams } from "react-router-dom";
import {
  add_blog,
  get_Active_Categories_by_feature_slug,
  update_blog,
} from "../../../../../DAL/blogs/BlogList";
import { useIds } from "../../../../../hook/AdminContext";

const AddOrUpdateInsightModal = ({
  open,
  onClose,
  handleGetInsightsList = () => {},
  editData = null, // if not null, update mode
}) => {
  const { source_slug, feature_slug } = useParams();
  const { userInfo } = useIds();
  const [inputs, setInputs] = useState({
    title: "",
    image: "",
    video_url: "",
    status: true,
    description: "",
    short_description: "",
    is_show_on_slider: false,
    writer: "",
    // add other default fields your ClientProfileFields expects
  });
  const [isSubmittingForm, setIsSubmittinForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [existingDocs, setExistingDocs] = useState([]);
  const [files, setFiles] = useState([]);
  const countCharacters = (str) => {
    return str ? str.length : 0;
  };
  const charLimit = 100;
  const charCount = countCharacters(inputs.short_description);
  const isOverLimit = charCount > charLimit;

  const isUpdateMode = Boolean(editData);

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleChangeDocs = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel", // .xls (Old Excel)
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx (New Excel)
      "text/csv", // .csv (CSV Files)
    ];

    const validFiles = selectedFiles.filter((file) =>
      allowedTypes.includes(file.type)
    );

    if (validFiles.length !== selectedFiles.length) {
      enqueueSnackbar("Only PDF, DOC, and DOCX files are allowed", {
        variant: "error",
      });
    }
    setFiles((prev) => [...prev, ...validFiles]);
    // Reset input
    e.target.value = null;
  };
  const removeFile = (indexToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleChangeImage = (e) => {
    if (e.target.files.length > 0) {
      setInputs((prevState) => ({
        ...prevState,
        image: e.target.files[0],
      }));
    }
    setTimeout(() => {
      e.target.value = "";
    }, 100);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isOverLimit) {
      enqueueSnackbar(
        `Short description length must be less than or equal to 100 characters long.`,
        { variant: "error" }
      );
      return;
    }
    if (!inputs.description?.trim()) {
      enqueueSnackbar(`Description is required`, { variant: "error" });
      return;
    }
    if (!inputs.image) {
      enqueueSnackbar(`Image is required`, { variant: "error" });
      return;
    }

    setIsSubmittinForm(true);
    let uploadedFiles = [...existingDocs];
    for (let file of files) {
      const formData = new FormData();
      formData.append("image", file);
      try {
        const res = await upload_image_documents_on_s3(formData);
        if (res.code === 200) {
          uploadedFiles.push(res.image_path);
        } else {
          enqueueSnackbar(`Failed to upload ${file.name}`, {
            variant: "error",
          });
        }
      } catch (err) {
        enqueueSnackbar(`Failed to upload ${file.name}`, { variant: "error" });
      }
    }

    const formData = new FormData();
    formData.append("title", inputs.title);
    formData.append("writer", inputs.writer);
    formData.append("short_description", inputs.short_description);
    formData.append("description", inputs.description);
    formData.append("status", inputs.status);
    formData.append("video_url", inputs.video_url);
    formData.append("is_show_on_slider", inputs.is_show_on_slider);
    formData.append("documents", JSON.stringify(uploadedFiles));

    formData.append("source", JSON.stringify([source_slug]));
    formData.append("feature", JSON.stringify([feature_slug]));
    if (inputs.image && inputs.image instanceof File) {
      formData.append("image", inputs.image);
    }
    formData.append("category", JSON.stringify([selectedCategory.title_slug]));
    if (isUpdateMode) {
      formData.append("order", inputs.order);
    } else {
      formData.append("created_by", "client");
      formData.append("client_id", userInfo.profile_id);
      if (userInfo.isCompanyProfile) {
        formData.append("action_id", userInfo._id);
      }
    }

    const resp = isUpdateMode
      ? await update_blog(editData.slug, formData)
      : await add_blog(formData);
    if (resp.code == 200) {
      onClose();
      handleGetInsightsList();
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setIsSubmittinForm(false);
  };

  const handleGetCategories = async () => {
    const resp = await get_Active_Categories_by_feature_slug(
      source_slug,
      feature_slug
    );
    if (resp.code == 200) {
      setCategories(resp.categories);
      if (isUpdateMode && editData?.category[0]?.category_id) {
        setSelectedCategory(
          resp.categories.find(
            (obj) => obj._id == editData?.category[0]?.category_id
          )
        );
      }
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
  };

  useEffect(() => {
    handleGetCategories();
    if (isUpdateMode && editData) {
      setInputs({
        title: editData.title,
        image: editData.image[0].thumbnail_2,
        order: editData.order,
        video_url: editData.video_url,
        status: editData.status,
        description: editData.description,
        short_description: editData.short_description,
        is_show_on_slider: editData.is_show_on_slider,
        writer: editData.writer || "",
      });
      setExistingDocs(editData.documents || []);
    } else {
      setInputs({
        title: "",
        image: "",
        video_url: "",
        status: true,
        description: "",
        short_description: "",
        is_show_on_slider: false,
        writer: userInfo?.company_profile?.company_name || "",
      });
    }
  }, [editData, isUpdateMode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ component: "form", onSubmit: handleSubmit }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>{isUpdateMode ? "Update Insight" : "Add New Insight"}</div>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <div className="row">
          <div className="col-lg-6 col-md-6 col-sm-12">
            <TextField
              id="outlined-basic"
              label="Title"
              variant="outlined"
              fullWidth
              required
              name="title"
              value={inputs.title}
              onChange={handleChange}
            />
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <TextField
              id="outlined-basic"
              label="Writer"
              variant="outlined"
              fullWidth
              required
              name="writer"
              value={inputs.writer}
              onChange={handleChange}
            />
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12 mt-4">
            <TextField
              id="outlined-basic"
              label="Video URL"
              variant="outlined"
              fullWidth
              name="video_url"
              value={inputs.video_url}
              onChange={handleChange}
            />
          </div>

          <div className="col-lg-6 col-md-6 col-sm-12 mt-4">
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Status *</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="status"
                value={inputs.status}
                label="Status *"
                onChange={handleChange}
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* <div className="col-lg-6 col-md-6 col-sm-12 mt-4">
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label-slider">
                Show On Slider *
              </InputLabel>
              <Select
                labelId="demo-simple-select-label-slider"
                id="demo-simple-select-slider"
                name="is_show_on_slider"
                value={inputs.is_show_on_slider}
                label="Show On Slider *"
                onChange={handleChange}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
          </div> */}

          <div className="col-lg-6 col-md-6 col-sm-12 mt-4">
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option?.title || ""}
              value={selectedCategory}
              onChange={(event, newValue) =>
                setSelectedCategory(
                  newValue
                    ? {
                        ...newValue,
                        category_id: newValue?.category_id || newValue?._id,
                        title_slug:
                          newValue?.title_slug ||
                          newValue?.category_slug ||
                          newValue?.slug,
                      }
                    : null
                )
              }
              isOptionEqualToValue={(opt, val) =>
                (opt?._id ||
                  opt?.category_id ||
                  opt?.title_slug ||
                  opt?.category_slug ||
                  opt?.slug) ===
                (val?._id ||
                  val?.category_id ||
                  val?.title_slug ||
                  val?.category_slug ||
                  val?.slug)
              }
              renderInput={(params) => (
                <TextField {...params} label="Choose Category" fullWidth />
              )}
            />
          </div>
          {/* {isUpdateMode && (
            <div className="col-lg-6 col-md-6 col-sm-12 mt-4">
              <TextField
                id="outlined-basic"
                label="Order"
                required
                variant="outlined"
                fullWidth
                name="order"
                value={inputs.order}
                onChange={handleChange}
              />
            </div>
          )} */}

          <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
            <div className="row w-100 div-style ms-0 pt-0">
              <div className="col-lg-5 col-md-5 col-sm-12">
                <p className="upload-text mb-0 mt-1">Upload Image*</p>
                <FormHelperText className="pt-0 ">
                  Image Size(1000 X 670) ("JPG", "JPEG", "PNG","WEBP")
                </FormHelperText>
              </div>
              <div className="col-lg-2 col-md-2 col-6">
                {inputs.image && (
                  <img
                    className="image-border"
                    src={
                      inputs.image instanceof File
                        ? URL.createObjectURL(inputs.image)
                        : s3baseUri + inputs.image
                    }
                    height={50}
                    alt="preview"
                  />
                )}
              </div>
              <div className="col-lg-5 col-md-5 col-6 text-end pt-2 pe-0">
                <label htmlFor="upload-button">
                  <input
                    className="small-contained-button-field"
                    accept="image/*"
                    id="upload-button"
                    type="file"
                    name="image"
                    style={{ display: "none" }}
                    onChange={handleChangeImage}
                  />

                  <button
                    className="btn-contained"
                    type="button"
                    onClick={() =>
                      document.getElementById("upload-button").click()
                    }
                  >
                    <FileUploadIcon sx={{ color: "white" }} className="me-2" />
                    {inputs.image ? "Change" : "Upload"}
                  </button>
                </label>
              </div>
            </div>
          </div>

          <div className="col-12 mt-4 mb-2">
            <Divider />
          </div>
          <div className="col-lg-12 col-md-12 col-sm-12 mt-3">
            <div className="row w-100 div-style ms-0 pt-0">
              <div className="col-lg-5 col-md-5 col-sm-12">
                <p className="upload-text mb-0 mt-1">Upload Documents</p>
                <FormHelperText className="pt-0">
                  Allowed formats: PDF, DOC, DOCX, XLS, XLSX
                </FormHelperText>
              </div>

              <div className="col-lg-2 col-md-2 col-12">
                {/* Existing documents */}
                {existingDocs.length > 0 &&
                  existingDocs.map((docUrl, index) => (
                    <div
                      key={`existing-${index}`}
                      className="d-flex align-items-center justify-content-between bg-light p-2 rounded mb-2"
                    >
                      <span
                        className="small text-truncate"
                        style={{ maxWidth: "80%" }}
                      >
                        {docUrl.split("/").pop()}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          const newDocs = [...existingDocs];
                          newDocs.splice(index, 1);
                          setExistingDocs(newDocs);
                        }}
                        style={{
                          padding: "3px 8px",
                          lineHeight: "1",
                          fontWeight: "bold",
                          borderRadius: "50%",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                {files.length > 0 &&
                  files.map((file, index) => (
                    <div
                      key={`new-${index}`}
                      className="d-flex align-items-center justify-content-between bg-light p-2 rounded mb-2"
                    >
                      <span
                        className="small text-truncate"
                        style={{ maxWidth: "80%" }}
                      >
                        {file.name}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeFile(index)}
                        style={{
                          padding: "3px 6px",
                          lineHeight: "1.2",
                          fontWeight: "bold",
                          borderRadius: "50%",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>

              <div className="col-lg-5 col-md-5 col-6 text-start text-md-end pt-2 pe-0">
                <label htmlFor="upload-file">
                  <input
                    className="small-contained-button-field"
                    accept=".pdf,.doc,.docx,.xlsx,.xls" // Excel files added here
                    id="upload-file"
                    multiple
                    type="file"
                    name="documents"
                    onChange={handleChangeDocs}
                    style={{ display: "none" }}
                  />

                  <button
                    className="btn-contained"
                    type="button"
                    onClick={() =>
                      document.getElementById("upload-file").click()
                    }
                  >
                    <FileUploadIcon sx={{ color: "white" }} className="me-2" />
                    Upload
                  </button>
                </label>
              </div>
            </div>
          </div>

          <div className="col-12 mt-5">
            <FormControl fullWidth error={isOverLimit}>
              <TextField
                id="outlined-multiline-flexible"
                label="Short Description"
                multiline
                required
                rows={2}
                name="short_description"
                value={inputs.short_description}
                onChange={handleChange}
                error={isOverLimit} // this makes red border appear
                helperText={
                  isOverLimit
                    ? "You can only add up to 100 characters"
                    : ` ${charCount}/${charLimit} characters`
                }
              />
            </FormControl>
          </div>

          <div className="col-12 mt-4 ckeditor">
            <h4>Detail Description *</h4>

            <GeneralCkeditor
              required
              inputs={inputs}
              setInputs={setInputs}
              onChange={handleChange}
              name="description"
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions className="px-4 py-3">
        <button className="btn-outlined" onClick={onClose} color="inherit">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmittingForm}
          variant="contained"
          color="primary"
          className="btn-contained"
        >
          {isSubmittingForm
            ? "Submitting..."
            : isUpdateMode
            ? "Update Insight"
            : "Add Insight"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrUpdateInsightModal;
